import React, { useContext, useState } from "react"
import { useStateMachine } from "../../../../context/stateMachine/StateMachineV2"
import { FormattedMessage, useIntl } from "react-intl"
import { Box, Link, Stack, Typography, useTheme } from "@carrotfertility/carotene-core"
import { Form, FormTextField, useFormContext } from "@carrotfertility/carotene-core-x"
import { Controller } from "react-hook-form"
import { CarrotCardCostSharePreAuthContext } from "../../../state/CarrotCardCostSharePreAuthContext"
import { StepButtons } from "#/components/carrot-card-cost-share/steps/shared/StepButtons"
import { NumericFormat } from "react-number-format"
import { resolveCurrencyFormat } from "#/utils/intl-currency"
import { usePreAuthAnswers } from "#/components/carrot-card-cost-share/state/usePreAuthAnswers"
import { useCurrentUser } from "#/components/context/user/UserContext"
import {
  AlertAndCheckbox,
  journeyTypeWithAlertAndCheckbox
} from "#/components/carrot-card-cost-share/steps/views/totalCost/AlertAndCheckbox"
import { JourneyTypes } from "#/components/get-reimbursed/steps/ExpenseJourneyTypes"
import { DbgPaths } from "#/utils/Paths"

export function TotalCostStep(): JSX.Element {
  const theme = useTheme()
  const { back, send } = useStateMachine()
  const { dispatchPreAuthAnswersResponseState, dispatchFormState, formState } = useContext(
    CarrotCardCostSharePreAuthContext
  )
  const [isConfirmed, setIsConfirmed] = useState(false)
  const { mutate: submitPreAuthAnswers } = usePreAuthAnswers()

  function onSubmit(formValues: { [key: string]: string }): void {
    submitPreAuthAnswers(
      {
        ...formState,
        totalCost: Number(formValues.totalCost)
      },
      {
        onSuccess: (response) => {
          dispatchPreAuthAnswersResponseState({
            type: "SET_RESPONSE",
            response
          })
          if (response.status === "DENIED") {
            send(response.deniedReason)
            return
          }
          send("")
        }
      }
    )
    dispatchFormState({
      type: "UPDATE_FIELD",
      field: "totalCost",
      value: formValues.totalCost
    })
  }

  return (
    <Box>
      <Typography id="total-cost-title" variant="h3" sx={(theme) => ({ marginBlockEnd: theme.tokens.spacing.xs })}>
        <FormattedMessage defaultMessage="What is the total cost?" />
      </Typography>

      <Stack spacing={theme.tokens.spacing.xl}>
        <Typography>
          <FormattedMessage defaultMessage="Ask the provider or merchant for the total cost, including taxes and fees, in the currency you'll use to pay." />
        </Typography>
        <Typography>
          <FormattedMessage defaultMessage="This amount must be exact for your Carrot Card to work." />
        </Typography>
        <Typography>
          <Link color="inherit" href={DbgPaths.CARROT_ELIGIBLE} target="_blank" rel="noopener noreferrer">
            <FormattedMessage defaultMessage="Review what's eligible." />
          </Link>
        </Typography>

        <Form
          onSubmit={onSubmit}
          defaultValues={{
            totalCost: formState.totalCost?.toString() ?? ""
          }}
        >
          <Stack spacing={theme.tokens.spacing.xl}>
            <TotalCostInput />

            <AlertAndCheckbox isConfirmed={isConfirmed} setIsConfirmed={setIsConfirmed} />
            <StepButtons
              selectName="totalCost"
              back={back}
              disabled={!isConfirmed && journeyTypeWithAlertAndCheckbox.includes(formState.journeyType as JourneyTypes)}
            />
          </Stack>
        </Form>
      </Stack>
    </Box>
  )
}

function TotalCostInput(): JSX.Element {
  const intl = useIntl()
  const { locale } = intl
  const { control } = useFormContext()
  const {
    company: { currencyCode }
  } = useCurrentUser()

  const format = resolveCurrencyFormat(locale, currencyCode)
  function getPrefixAndSuffix(position: "prefix" | "suffix"): string {
    return format?.currencyPosition === position ? ` ${format?.currencySymbol}` : ""
  }

  return (
    <Controller
      name="totalCost"
      control={control}
      rules={{ required: true }}
      render={({ field: { onChange, value } }) => (
        <NumericFormat
          id="totalCost"
          name="totalCost"
          customInput={FormTextField}
          placeholder={intl.formatMessage({ defaultMessage: "Total amount" })}
          value={value}
          onValueChange={(values) => {
            onChange(values.value)
          }}
          allowNegative={false}
          prefix={getPrefixAndSuffix("prefix")}
          suffix={getPrefixAndSuffix("suffix")}
          thousandSeparator={format?.groupDelimiter}
          decimalSeparator={format?.decimalDelimiter}
          decimalScale={format?.maximumFractionDigits}
        />
      )}
    />
  )
}
